import type { IntervalJSON } from '@highimpact-frontend/api-types';

import { resolveIntervals } from '../convertInterval';
import { DEFAULT_RANGE_BOUND_TOLERANCE_MS } from './const';

/** Simple start/end string type to represent the range bounds */
export interface RangeBounds {
  start: string;
  end: string;
}

/** 
 * Resolve the default bounds from the interval list.
 * 
 * - If the interval list is empty, return null.
 * - If the interval list does not resolve to a valid span, return null.
 * - Otherwise, return the start and end bounds.
 * 
 * @param defaultRange - The list of intervals to resolve the bounds from.
 * @returns The resolved bounds or null if the interval list is empty or does not resolve to a valid span.
 */
export function resolveDefaultBounds(
  defaultRange: IntervalJSON[]
): RangeBounds | null {
  if (!defaultRange.length) {
    return null;
  }

  const span = resolveIntervals(defaultRange);
  if (!span) {
    return null;
  }

  return { start: span[0], end: span[1] };
}

/** Convert resolved bounds to millisecond tuple for Plotly date axes. */
export function rangeBoundsToTimeSelectionMs(
  bounds: RangeBounds
): [number, number] | undefined {
  const startMs = new Date(bounds.start).getTime();
  const endMs = new Date(bounds.end).getTime();

  if (Number.isNaN(startMs) || Number.isNaN(endMs)) {
    return undefined;
  }

  return [startMs, endMs];
}


/** Check if the snapshot (i.e.: the current data in memory) covers the target range. */
export function snapshotCoversRange<TRow>(
  rows: TRow[] | null | undefined,
  getTimestamp: (row: TRow) => string,
  target: RangeBounds
): boolean {
  if (!rows?.length) {
    return false;
  }

  let minMs = Number.POSITIVE_INFINITY;
  let maxMs = Number.NEGATIVE_INFINITY;

  for (const row of rows) {
    const timestampMs = new Date(getTimestamp(row)).getTime();
    if (Number.isNaN(timestampMs)) {
      continue;
    }

    minMs = Math.min(minMs, timestampMs);
    maxMs = Math.max(maxMs, timestampMs);
  }

  if (!Number.isFinite(minMs) || !Number.isFinite(maxMs)) {
    return false;
  }

  const targetStartMs = new Date(target.start).getTime();
  const targetEndMs = new Date(target.end).getTime();

  if (Number.isNaN(targetStartMs) || Number.isNaN(targetEndMs)) {
    return false;
  }

  return minMs <= targetStartMs && maxMs >= targetEndMs;
}

/** Compare two range upper bounds within a tolerance. */
export function rangeEndsEqualWithinTolerance(
  endA: string,
  endB: string,
  toleranceMs: number = DEFAULT_RANGE_BOUND_TOLERANCE_MS
): boolean {
  const endAMs = new Date(endA).getTime();
  const endBMs = new Date(endB).getTime();

  if (Number.isNaN(endAMs) || Number.isNaN(endBMs)) {
    return false;
  }

  return Math.abs(endAMs - endBMs) <= toleranceMs;
}

/**
 * Check whether refresh should extend the visible range upper bound to match the new loaded data.
 *
 * True when the user is viewing through the loaded span's upper edge (visible end ~= loaded end)
 * and the loaded span does not extend into the future (no predictive data window).
 */
export function shouldExtendVisibleRangeOnRefresh(
  visibleRange: RangeBounds,
  loadedRange: RangeBounds,
  nowMs: number = Date.now(),
  toleranceMs: number = DEFAULT_RANGE_BOUND_TOLERANCE_MS
): boolean {
  const loadedEndMs = new Date(loadedRange.end).getTime();

  if (Number.isNaN(loadedEndMs)) {
    return false;
  }

  if (loadedEndMs > nowMs) {
    return false;
  }

  return rangeEndsEqualWithinTolerance(
    visibleRange.end,
    loadedRange.end,
    toleranceMs
  );
}
